/**
 * Phase 8c — AL storefront rewrite layer (Scrubber).
 *
 * Der Scrubber fängt AUSSCHLIESSLICH CH-spezifische Begriffe ab
 * (Schweiz, TWINT, CHF, bigfollow.ch), die auch in gepflegten Texten
 * stehen können. Er ist KEIN Übersetzer: Übersetzungen gehören in
 * ui_translations('sq') bzw. in die *_translations-Overlays. Er läuft
 * NACH seo_overrides / DB-Overlays — Admin-Overrides gewinnen immer.
 *
 * HARTE REGELN:
 *   • Wird NUR aufgerufen, wenn storefront === 'al'. CH, AT und IT
 *     werden nie berührt (byte-identisch).
 *   • Längste Muster zuerst.
 *   • Feld-selektiv: Medien-, Preis-, ID-, URL-Felder werden NIE angefasst
 *     (SKIP_KEY_RE + looksLikeUrlOrAsset), exakt wie bei IT.
 *   • Rechtstexte (Impressum/AGB/Datenschutz) sind vom Scrubber
 *     AUSGENOMMEN — siehe isLegalPathAL().
 */

type Rule = { pattern: RegExp; replace: string | ((m: string) => string) };

const RULES: Rule[] = [
  // ---- Zusammengesetzte Länder-Phrasen (vor den kurzen Regeln) ----
  { pattern: /\bin der ganzen Schweiz\b/gi, replace: 'në të gjithë Shqipërinë' },
  { pattern: /\bder ganzen Schweiz\b/gi, replace: 'të gjithë Shqipërinë' },
  { pattern: /\bin tutta la Svizzera\b/gi, replace: 'në të gjithë Shqipërinë' },
  { pattern: /\btutta la Svizzera\b/gi, replace: 'në të gjithë Shqipërinë' },
  { pattern: /\bBest prices in Switzerland\b/gi, replace: 'Best prices in Albania' },

  // ---- Domain vor der Marke ----
  { pattern: /\bbigfollow\.ch\b/g, replace: 'bigfollow.al' },
  { pattern: /\bBigFollow\.ch\b/g, replace: 'BigFollow' },
  { pattern: /\bBIGFOLLOW\.CH\b/g, replace: 'BIGFOLLOW' },

  // ---- Zahlung: TWINT existiert auf AL nicht → PayPal ----
  { pattern: /\bTWINT\b/gi, replace: 'PayPal' },

  // ---- Währung: AL rechnet in EUR (Loyalty ist auf AL abgeschaltet,
  //      es gibt daher keine CHF-Punkte-Beträge, die verfälscht würden). ----
  { pattern: /\bSchweizer Franken\b/gi, replace: 'euro' },
  { pattern: /\bCHF\b/g, replace: 'EUR' },

  // ---- Ländernomen zuletzt (kürzeste, gierigste Regeln) ----
  { pattern: /\bSchweizer\b/g, replace: 'shqiptar' },
  { pattern: /\bSchweiz\b/g, replace: 'Shqipëri' },
  { pattern: /\bSvizzera\b/g, replace: 'Shqipëri' },
  { pattern: /\bsvizzera\b/g, replace: 'shqiptare' },
  { pattern: /\bsvizzero\b/g, replace: 'shqiptar' },
  { pattern: /\bSwitzerland\b/g, replace: 'Albania' },
  { pattern: /\bSuisse\b/g, replace: 'Albanie' },

  // ---- Albanische CH-Reste (aus maschinellen Übersetzungen) ----
  { pattern: /\bZvicrës\b/g, replace: 'Shqipërisë' },
  { pattern: /\bZvicra\b/g, replace: 'Shqipëria' },
  { pattern: /\bZvicër\b/g, replace: 'Shqipëri' },
  { pattern: /\bzviceran(e|ë)?\b/gi, replace: 'shqiptar' },

  // ---- Verbform vereinheitlichen: Live-Shop nutzt durchgängig "Blej" ----
  { pattern: /\bBlini\b/g, replace: 'Blej' },
  { pattern: /\bBli\b/g, replace: 'Blej' },
  { pattern: /\bblini\b/g, replace: 'blej' },
];

// Cleanup: doppelte PayPal-Nennungen (TWINT + PayPal in derselben Liste),
// Whitespace, Pipe-Separatoren.
const CLEANUP: Rule[] = [
  { pattern: /\bPayPal\s*([,/])\s*PayPal\b/gi, replace: 'PayPal' },
  { pattern: /\bPayPal\s+PayPal\b/gi, replace: 'PayPal' },
  { pattern: /\s{2,}/g, replace: ' ' },
  { pattern: /\s+\|/g, replace: ' |' },
  { pattern: /\|\s*\|/g, replace: '|' },
  { pattern: /^\s*\|\s*/, replace: '' },
  { pattern: /\s*\|\s*$/, replace: '' },
  { pattern: /\s+([,.;:!?])/g, replace: '$1' },
  { pattern: /^\s+|\s+$/g, replace: '' },
];

export function rewriteForAL(input: string | null | undefined): string {
  if (!input || typeof input !== 'string') return input ?? '';
  let out = input;
  for (const r of RULES) out = out.replace(r.pattern, r.replace as any);
  for (const r of CLEANUP) out = out.replace(r.pattern, r.replace as any);
  return out;
}

/**
 * Technische Felder (URLs, IDs, Slugs, Dateinamen, Preise, Farben,
 * Timestamps) — NIE umschreiben.
 */
const SKIP_KEY_RE =
  /(^|_)(id|slug|url|src|href|path|icon|image_url|image|thumbnail|avatar|media|video|audio|file|filename|price|amount|currency|color|hex|token|hash|uuid|created_at|updated_at|_at|_by)($|_)/i;

function looksLikeUrlOrAsset(s: string): boolean {
  if (s.length > 4096) return false;
  if (/^(https?:)?\/\//i.test(s)) return true;
  if (/^data:/i.test(s)) return true;
  if (/\/storage\/v1\//.test(s)) return true;
  if (!/\s/.test(s) && /\.(png|jpe?g|gif|webp|svg|avif|mp4|webm|mp3|pdf|ico|woff2?)$/i.test(s)) return true;
  return false;
}

export function rewriteObjectForAL<T>(obj: T): T {
  if (obj == null) return obj;
  if (typeof obj === 'string') {
    return (looksLikeUrlOrAsset(obj) ? obj : rewriteForAL(obj)) as unknown as T;
  }
  if (Array.isArray(obj)) return obj.map(rewriteObjectForAL) as unknown as T;
  if (typeof obj === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      if (typeof v === 'string' && SKIP_KEY_RE.test(k)) out[k] = v;
      else out[k] = rewriteObjectForAL(v);
    }
    return out as T;
  }
  return obj;
}

/**
 * Rechtstexte sind vom Scrubber AUSGENOMMEN: dort stehen die Schweizer
 * Firmenangaben (PortalWeb GmbH, Kornmattstrasse 24, 6460 Altdorf,
 * CHE-403.309.045) WÖRTLICH und dürfen nicht "albanisiert" werden.
 */
export const AL_LEGAL_SEGMENTS = ['impresum', 'kushtet', 'privatesia', 'impressum', 'agb', 'datenschutz'];

export function isLegalPathAL(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  const parts = pathname.toLowerCase().split('/').filter(Boolean);
  return parts.some((p) => AL_LEGAL_SEGMENTS.includes(p));
}

/** True nur auf sf='al' UND ausserhalb der Legal-Routen. */
export function shouldScrubForAL(storefront: string, pathname?: string | null): boolean {
  if (storefront !== 'al') return false;
  const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  return !isLegalPathAL(path);
}
